import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { serviceOffersLd, breadcrumbLd } from "@/lib/jsonld";
import { usePageContent, ContentLoading } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Structuur van de kaarten (links, varianten) blijft in de code; alle teksten
// komen uit de database. DEFAULTS is de terugval als de content-API faalt.
const CARD_STRUCTURE = [
    { link: "/boeken?sessie=hd-sessie-basis", variant: "primary", detail: "/aanbod/hd-sessie-basis", hasNote: false },
    { link: "/boeken?sessie=hd-sessie-uitgebreid", variant: "primary", detail: "/aanbod/hd-sessie-uitgebreid", hasNote: false },
    { link: "/boeken?sessie=verdiepingssessie", variant: "primary", detail: "/aanbod/verdiepingssessie", hasNote: false },
    { link: "/boeken?sessie=hd-traject", variant: "primary", detail: "/aanbod/hd-traject", hasNote: true },
    { variant: "waitlist", detail: "/aanbod/avond-workshop", hasNote: true },
    { link: "/gratis-qa", variant: "secondary", detail: "/gratis-qa", hasNote: false },
];

const DEFAULTS = {
    "aanbod.seo-title": "Aanbod en sessies | Human Design Studio",
    "aanbod.seo-description":
        "Human Design sessies, trajecten en een workshop. Bekijk het aanbod met prijzen en boek je sessie.",
    "aanbod.hero.eyebrow": "Aanbod",
    "aanbod.hero.h1": "Aanbod",
    "aanbod.hero.intro":
        "Elke sessie start bij jouw unieke bodygraph. We kijken naar je kwaliteiten, je talenten en je uitdagingen, en vooral naar hoe je daar in het dagelijks leven iets mee doet.",
    "aanbod.hero.online-note": "Sessies zijn online via Google Meet.",
    "aanbod.uitleg.heading": "Wat houdt een sessie in?",
    "aanbod.uitleg.p1":
        "In een sessie kijken we samen naar jouw bodygraph, je persoonlijke blauwdruk. Ik leg uit wat je type is en hoe jij van nature het beste beslissingen neemt. We staan stil bij je kwaliteiten, je talenten en je uitdagingen.",
    "aanbod.uitleg.p2":
        "Maar ik wil niet dat je alleen informatie krijgt. Ik help je echt contact maken met wat we tegenkomen. Daarvoor gebruik ik naast Human Design ook NLP, lichaamswerk en familieopstellingen. Zo ga je het niet alleen begrijpen, maar ook voelen en toepassen in je dagelijks leven.",
    "aanbod.uitleg.p3":
        "De sessie is online via Google Meet en duurt 1 of 2 uur, afhankelijk van de vorm. Vooraf gebruik ik je geboortegegevens om je bodygraph te maken.",
    "aanbod.uitleg.lijst-label": "Wat je krijgt",
    "aanbod.uitleg.lijst": [
        "Inzicht in je type en je manier van beslissen (je autoriteit).",
        "Zicht op je sterke punten en je uitdagingen.",
        "Praktische handvatten om ermee verder te gaan.",
        "Ruimte voor jouw vragen. Het is jouw sessie.",
    ],
    "aanbod.kaart-1.titel": "HD sessie basis",
    "aanbod.kaart-1.duur": "1 uur",
    "aanbod.kaart-1.prijs": "95 euro",
    "aanbod.kaart-1.beschrijving":
        "Uitleg aan de hand van jouw unieke bodygraph: je kwaliteiten, je talenten en je uitdagingen. De belangrijkste zaken, in een behapbare vorm, zodat je er praktisch mee verder kunt.",
    "aanbod.kaart-1.knop": "Boek een sessie",
    "aanbod.kaart-2.titel": "HD sessie uitgebreid",
    "aanbod.kaart-2.duur": "2 uur aaneengesloten",
    "aanbod.kaart-2.prijs": "195 euro",
    "aanbod.kaart-2.beschrijving":
        "Verdieping op de basissessie. Er is ruimte om dieper te gaan en meer verbanden te leggen. De volgende stap in je Human Design experiment.",
    "aanbod.kaart-2.knop": "Boek een sessie",
    "aanbod.kaart-3.titel": "Verdiepingssessie",
    "aanbod.kaart-3.duur": "1 uur",
    "aanbod.kaart-3.prijs": "95 euro",
    "aanbod.kaart-3.beschrijving":
        "Loop je ergens tegenaan in je leven of in je experiment? Boek een losse sessie en krijg 1-op-1 hulp bij jouw vraag.",
    "aanbod.kaart-3.knop": "Boek een sessie",
    "aanbod.kaart-4.titel": "HD traject",
    "aanbod.kaart-4.duur": "10 sessies van een uur",
    "aanbod.kaart-4.prijs": "995 euro",
    "aanbod.kaart-4.beschrijving":
        "Meerdere sessies over een langere periode, zodat ik je optimaal kan bijstaan in je Human Design experiment. Voor wie echt stappen wil zetten.",
    "aanbod.kaart-4.note": "Je plant nu je eerste sessie. De overige stemmen we samen af.",
    "aanbod.kaart-4.knop": "Boek een sessie",
    "aanbod.kaart-5.titel": "Avond workshop",
    "aanbod.kaart-5.duur": "een avond, max 6 personen",
    "aanbod.kaart-5.prijs": "295 euro per persoon",
    "aanbod.kaart-5.beschrijving":
        "Een avond over Human Design in een kleine groep. Uitleg over het systeem, je eigen blauwdruk komt aan bod en er is veel ruimte voor vragen.",
    "aanbod.kaart-5.note": "Nog geen vaste data. Je hoort het als eerste zodra een avond gepland is.",
    "aanbod.kaart-5.knop": "Zet me op de wachtlijst",
    "aanbod.kaart-6.titel": "Gratis Q&A",
    "aanbod.kaart-6.duur": "online via Google Meet",
    "aanbod.kaart-6.prijs": "gratis",
    "aanbod.kaart-6.beschrijving":
        "Elke twee weken een gratis online sessie. Haak aan, stel je vraag en deel met anderen.",
    "aanbod.kaart-6.knop": "Aanmelden",
    "aanbod.meer-info": "Meer informatie",
    "aanbod.cta.heading": "Niet zeker welke sessie bij je past?",
    "aanbod.cta.text":
        "Geen probleem. Boek gerust een sessie, of stel eerst je vraag. Dan kijken we samen wat past.",
    "aanbod.cta.boek": "Boek een sessie",
    "aanbod.cta.vraag": "Stel je vraag",
};

export default function Aanbod() {
    const c = usePageContent("aanbod", DEFAULTS);
    useSeo({
        title: c ? c.t("aanbod.seo-title") : DEFAULTS["aanbod.seo-title"],
        description: c
            ? c.t("aanbod.seo-description")
            : DEFAULTS["aanbod.seo-description"],
        path: "/aanbod",
        jsonLd: [serviceOffersLd(), breadcrumbLd("Aanbod", "/aanbod")],
    });
    const [uitleg, setUitleg] = useState(false);

    if (!c) return <ContentLoading />;

    const cards = CARD_STRUCTURE.map((s, i) => ({
        ...s,
        title: c.t(`aanbod.kaart-${i + 1}.titel`),
        duration: c.t(`aanbod.kaart-${i + 1}.duur`),
        price: c.t(`aanbod.kaart-${i + 1}.prijs`),
        description: c.t(`aanbod.kaart-${i + 1}.beschrijving`),
        note: s.hasNote ? c.t(`aanbod.kaart-${i + 1}.note`) : null,
        buttonLabel: c.t(`aanbod.kaart-${i + 1}.knop`),
    }));
    const uitlegLijst = c.list("aanbod.uitleg.lijst");

    return (
        <div data-testid="page-aanbod">
            {/* ============ HERO ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 pt-24 md:pt-40 pb-14 md:pb-20">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-6"
                >
                    {c.t("aanbod.hero.eyebrow")}
                </motion.p>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end">
                    <div className="md:col-span-6 overflow-hidden">
                        <motion.h1
                            initial={{ y: "110%" }}
                            animate={{ y: "0%" }}
                            transition={{ duration: 1.05, ease: EASE, delay: 0.05 }}
                            className="font-[Fraunces] text-[16vw] md:text-[9vw] lg:text-[96px] leading-[0.98] tracking-[-0.02em] text-[var(--hds-ink)]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("aanbod.hero.h1")}
                        </motion.h1>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: EASE, delay: 0.45 }}
                        className="md:col-span-6 max-w-[58ch]"
                    >
                        <p className="text-[16.5px] md:text-[18px] leading-[1.65] text-[var(--hds-ink)]/82">
                            {c.t("aanbod.hero.intro")}
                        </p>
                        <p
                            data-testid="aanbod-online-note"
                            className="mt-4 text-[14.5px] text-[var(--hds-ink)]/60"
                        >
                            {c.t("aanbod.hero.online-note")}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ============ WAT HOUDT EEN SESSIE IN? ============ */}
            <section
                data-testid="aanbod-uitleg"
                className="mx-auto max-w-[1440px] px-5 md:px-10 pb-8 md:pb-12"
            >
                <div className="rounded-[14px] border border-[var(--hds-line)] bg-[var(--hds-card)]/70 overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setUitleg((v) => !v)}
                        aria-expanded={uitleg}
                        aria-controls="aanbod-uitleg-panel"
                        data-testid="aanbod-uitleg-toggle"
                        className="w-full flex items-center justify-between gap-4 px-7 md:px-9 py-6 md:py-7 text-left group"
                    >
                        <span
                            className="font-[Fraunces] text-[22px] md:text-[26px] tracking-[-0.01em] text-[var(--hds-ink)]"
                            style={{ fontWeight: 400 }}
                        >
                            {c.t("aanbod.uitleg.heading")}
                        </span>
                        <span
                            aria-hidden
                            className={`inline-flex w-10 h-10 items-center justify-center rounded-full border border-[var(--hds-line)] bg-[var(--hds-sand)] text-[var(--hds-clay)] transition-transform duration-500 ${uitleg ? "rotate-45" : ""}`}
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                            </svg>
                        </span>
                    </button>
                    <AnimatePresence initial={false}>
                        {uitleg && (
                            <motion.div
                                id="aanbod-uitleg-panel"
                                key="panel"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.5, ease: EASE }}
                                className="overflow-hidden border-t border-[var(--hds-line)]"
                            >
                                <div className="px-7 md:px-9 py-7 md:py-9 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
                                    <div className="md:col-span-7 text-[15.5px] md:text-[16.5px] leading-[1.65] text-[var(--hds-ink)]/82 space-y-4">
                                        <p>{c.t("aanbod.uitleg.p1")}</p>
                                        <p>{c.t("aanbod.uitleg.p2")}</p>
                                        <p className="text-[14px] text-[var(--hds-ink)]/60 pt-2">
                                            {c.t("aanbod.uitleg.p3")}
                                        </p>
                                    </div>
                                    <div className="md:col-span-5">
                                        <p className="text-[11.5px] tracking-[0.24em] uppercase text-[var(--hds-clay)] mb-4">
                                            {c.t("aanbod.uitleg.lijst-label")}
                                        </p>
                                        <ul
                                            data-testid="aanbod-uitleg-list"
                                            className="space-y-3"
                                        >
                                            {uitlegLijst.map((li, i) => (
                                                <li
                                                    key={i}
                                                    className="flex gap-3 text-[15px] leading-[1.6] text-[var(--hds-ink)]/82"
                                                >
                                                    <span
                                                        aria-hidden
                                                        className="mt-2.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--hds-clay)]"
                                                    />
                                                    <span>{li}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* ============ KAARTEN ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 py-10 md:py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {cards.map((item, i) => (
                        <AanbodCard
                            key={item.title}
                            item={item}
                            index={i}
                            meerInfoLabel={c.t("aanbod.meer-info")}
                        />
                    ))}
                </div>
            </section>

            {/* ============ CTA-BLOK ============ */}
            <section className="mx-auto max-w-[1440px] px-5 md:px-10 py-16 md:py-28 pb-24 md:pb-40">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-8%" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    data-testid="aanbod-cta-block"
                    className="relative overflow-hidden rounded-[18px] bg-[var(--hds-clay)] text-[var(--hds-card)] px-8 md:px-16 py-14 md:py-20"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -right-16 -top-16 w-[280px] h-[280px] rounded-full"
                        style={{
                            background:
                                "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
                        }}
                    />
                    <div className="relative max-w-[52ch]">
                        <h2
                            className="font-[Fraunces] text-[34px] md:text-[52px] leading-[1.05] tracking-[-0.02em] text-[var(--hds-card)]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("aanbod.cta.heading")}
                        </h2>
                        <p className="mt-6 text-[16px] md:text-[17.5px] leading-[1.6] text-[var(--hds-card)]/85 max-w-[46ch]">
                            {c.t("aanbod.cta.text")}
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Button
                                to="/boeken"
                                data-testid="aanbod-cta-boek"
                                variant="onClayPrimary"
                                icon="arrow"
                            >
                                {c.t("aanbod.cta.boek")}
                            </Button>
                            <Button
                                to="/contact"
                                data-testid="aanbod-cta-vraag"
                                variant="onClaySecondary"
                            >
                                {c.t("aanbod.cta.vraag")}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}

function AanbodCard({ item, index, meerInfoLabel }) {
    const mountRef = useRef(Date.now());
    const [showWaitlist, setShowWaitlist] = useState(false);
    const [form, setForm] = useState({ naam: "", email: "", privacy_akkoord: false, website: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [serverError, setServerError] = useState("");

    const isWaitlist = item.variant === "waitlist";

    const submitWaitlist = async () => {
        const e = {};
        if (!form.naam.trim()) e.naam = "Vul je naam in.";
        if (!emailOk(form.email)) e.email = "Vul een geldig e-mailadres in.";
        if (!form.privacy_akkoord)
            e.privacy_akkoord = "Je moet akkoord gaan om je aan te melden.";
        setErrors(e);
        if (Object.keys(e).length) return;
        setSubmitting(true);
        setServerError("");
        try {
            await axios.post(`${API}/workshop-waitlist`, {
                ...form,
                elapsed_ms: Date.now() - mountRef.current,
            });
            setDone(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setServerError(typeof d === "string" ? d : "Er ging iets mis. Probeer het opnieuw.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8%" }}
            transition={{
                duration: 0.8,
                ease: EASE,
                delay: (index % 3) * 0.08,
            }}
            data-testid={`aanbod-card-${index + 1}`}
            className="group flex flex-col rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-8 md:p-9 shadow-[0_20px_60px_rgba(44,39,34,0.04)] hover:shadow-[0_28px_80px_rgba(44,39,34,0.08)] transition-shadow duration-700"
        >
            <div className="flex items-baseline justify-between gap-3 mb-2">
                <h2
                    className="font-[Fraunces] text-[27px] md:text-[30px] leading-[1.08] tracking-[-0.01em] text-[var(--hds-ink)]"
                    style={{ fontWeight: 300 }}
                >
                    {item.title}
                </h2>
            </div>

            <div className="flex items-center gap-3 text-[12px] tracking-[0.16em] uppercase text-[var(--hds-ink)]/50 mb-5 pb-5 border-b border-[var(--hds-line)]">
                <span>{item.duration}</span>
                <span className="sr-only">, </span>
                <span
                    aria-hidden
                    className="inline-block w-1 h-1 rounded-full bg-[var(--hds-clay)]/60"
                />
                <span
                    className="font-[Fraunces] text-[18px] tracking-normal normal-case text-[var(--hds-clay)]"
                    style={{ fontWeight: 400, fontStyle: "italic" }}
                    data-testid={`aanbod-price-${index + 1}`}
                >
                    {item.price}
                </span>
            </div>

            <p className="text-[15.5px] md:text-[16px] leading-[1.62] text-[var(--hds-ink)]/80">
                {item.description}
            </p>

            {item.note && (
                <p
                    className="mt-4 text-[13.5px] leading-[1.55] text-[var(--hds-ink)]/55 italic"
                    data-testid={`aanbod-note-${index + 1}`}
                >
                    {item.note}
                </p>
            )}

            <div className="mt-8 pt-2 flex-1 flex flex-col justify-end">
                {isWaitlist ? (
                    done ? (
                        <div
                            data-testid="waitlist-confirmation"
                            className="rounded-[12px] bg-[var(--hds-green)]/10 border border-[var(--hds-green)]/30 p-4 flex gap-3 text-[14.5px] leading-[1.55] text-[var(--hds-ink)]"
                        >
                            <span className="mt-0.5 w-6 h-6 rounded-full bg-[var(--hds-green)] flex items-center justify-center text-[var(--hds-card)] shrink-0">
                                <Check size={14} strokeWidth={2} />
                            </span>
                            <span>
                                Je staat op de wachtlijst. Zodra er een avond
                                gepland is, laat ik het je als eerste weten.
                            </span>
                        </div>
                    ) : showWaitlist ? (
                        <div className="space-y-3">
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                                style={{ position: "absolute", left: "-9999px", opacity: 0 }}
                                aria-hidden
                                data-testid="waitlist-website"
                            />
                            <input
                                type="text"
                                placeholder="Naam"
                                value={form.naam}
                                onChange={(e) => setForm({ ...form, naam: e.target.value })}
                                data-testid="waitlist-naam"
                                className={`w-full rounded-[10px] bg-[var(--hds-sand)] border outline-none px-4 py-3 text-[14.5px] ${errors.naam ? "border-[var(--hds-clay)]" : "border-[var(--hds-line)] focus:border-[var(--hds-clay)]"}`}
                            />
                            {errors.naam && (
                                <p className="text-[12.5px] text-[var(--hds-clay-dark)]" data-testid="waitlist-naam-error">{errors.naam}</p>
                            )}
                            <input
                                type="email"
                                placeholder="E-mailadres"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="waitlist-email"
                                className={`w-full rounded-[10px] bg-[var(--hds-sand)] border outline-none px-4 py-3 text-[14.5px] ${errors.email ? "border-[var(--hds-clay)]" : "border-[var(--hds-line)] focus:border-[var(--hds-clay)]"}`}
                            />
                            {errors.email && (
                                <p className="text-[12.5px] text-[var(--hds-clay-dark)]" data-testid="waitlist-email-error">{errors.email}</p>
                            )}
                            <label className="flex items-start gap-2.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.privacy_akkoord}
                                    onChange={(e) => setForm({ ...form, privacy_akkoord: e.target.checked })}
                                    data-testid="waitlist-privacy"
                                    className="mt-1 w-4 h-4 accent-[var(--hds-clay)] shrink-0"
                                />
                                <span className="text-[13px] leading-[1.5] text-[var(--hds-ink)]/78">
                                    Ik ga akkoord met de{" "}
                                    <Link to="/privacy" className="text-[var(--hds-clay)] underline underline-offset-2">privacyverklaring</Link>.
                                </span>
                            </label>
                            {errors.privacy_akkoord && (
                                <p className="text-[12.5px] text-[var(--hds-clay-dark)]" data-testid="waitlist-privacy-error">{errors.privacy_akkoord}</p>
                            )}
                            {serverError && (
                                <p className="text-[13px] text-[var(--hds-clay-dark)]" data-testid="waitlist-server-error">{serverError}</p>
                            )}
                            <div className="flex flex-wrap gap-2 pt-1">
                                <Button
                                    variant="primary"
                                    icon="arrow"
                                    onClick={submitWaitlist}
                                    disabled={submitting}
                                    data-testid="waitlist-submit"
                                >
                                    {submitting ? "Bezig..." : "Aanmelden"}
                                </Button>
                                <button
                                    type="button"
                                    onClick={() => setShowWaitlist(false)}
                                    data-testid="waitlist-cancel"
                                    className="text-[13px] text-[var(--hds-ink)]/60 hover:text-[var(--hds-clay)] underline underline-offset-2"
                                >
                                    Annuleren
                                </button>
                            </div>
                        </div>
                    ) : (
                        <Button
                            variant="secondary"
                            onClick={() => setShowWaitlist(true)}
                            data-testid={`aanbod-waitlist-toggle-${index + 1}`}
                        >
                            {item.buttonLabel}
                        </Button>
                    )
                ) : (
                    <Button
                        to={item.link}
                        variant={item.variant}
                        icon={item.variant === "primary" ? "arrow" : "none"}
                        data-testid={`aanbod-cta-${index + 1}`}
                    >
                        {item.buttonLabel}
                    </Button>
                )}
                {item.detail && (
                    <Link
                        to={item.detail}
                        data-testid={`aanbod-meer-info-${index + 1}`}
                        className="mt-4 text-[13.5px] text-[var(--hds-ink)]/60 hover:text-[var(--hds-clay)] underline underline-offset-2 self-start"
                    >
                        {meerInfoLabel}
                    </Link>
                )}
            </div>
        </motion.article>
    );
}
